import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { Loading } from "../components/Loader";
import PermissionMatrix from "../components/PermissionMatrix";
import {
  deleteRole,
  fetchAccessResources,
  fetchRole,
  selectAccessResources,
  selectRole,
  updateRole,
} from "../features/access/accessSlice";
import { countReadable, matrixEquals } from "../features/access/matrix";
import { fetchMe, selectAuth } from "../features/auth/authSlice";
import { can } from "../features/auth/permissions";
import "./ManagementPages.css";

/**
 * Права роли — матрица «раздел × действие» (Role Permission Manager из ERPNext).
 *
 * Правки копятся в черновике и уходят одним PATCH по «Сохранить». Черновик — UI-состояние
 * (как редактируемая строка), поэтому он в useState, а сохранённая роль — в Redux.
 */
export default function RolePermissionsPage() {
  const { roleId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector(selectAuth);
  const registry = useSelector(selectAccessResources);
  const roleState = useSelector(selectRole);

  const role = roleState.data && String(roleState.data.id) === String(roleId) ? roleState.data : null;
  const canWrite = can(user, "role", "write");
  const canDelete = can(user, "role", "delete");

  const [draft, setDraft] = useState(null);
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (registry.status === "idle") dispatch(fetchAccessResources());
  }, [dispatch, registry.status]);

  useEffect(() => {
    dispatch(fetchRole(roleId));
  }, [dispatch, roleId]);

  // Черновик заново берётся из сохранённой роли: при открытии и после сохранения.
  useEffect(() => {
    if (!role) return;
    setDraft(role.permissions);
    setLabel(role.label);
    setDescription(role.description || "");
  }, [role]);

  const dirty = useMemo(() => {
    if (!role || !draft) return false;
    return !matrixEquals(draft, role.permissions) || label !== role.label || description !== (role.description || "");
  }, [role, draft, label, description]);

  // Уход со страницы (закрытие вкладки, перезагрузка) с несохранёнными правами.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const onMatrixChange = useCallback((next) => {
    setDraft(next);
    setSaved(false);
  }, []);

  const reset = () => {
    setDraft(role.permissions);
    setLabel(role.label);
    setDescription(role.description || "");
    setError(null);
  };

  const goBack = () => {
    if (dirty && !window.confirm("Есть несохранённые изменения. Уйти без сохранения?")) return;
    navigate("/management/roles");
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const result = await dispatch(updateRole({ id: role.id, label, description, permissions: draft }));
    setSaving(false);
    if (updateRole.fulfilled.match(result)) {
      setSaved(true);
      // Правили собственную роль — меню и доступы текущего пользователя должны обновиться сразу.
      if (user?.groups?.includes(role.name)) dispatch(fetchMe());
    } else {
      setError(result.payload);
    }
  };

  const remove = async () => {
    setConfirmDelete(false);
    const result = await dispatch(deleteRole(role.id));
    if (deleteRole.fulfilled.match(result)) navigate("/management/roles", { replace: true });
    else setError(result.payload);
  };

  if (roleState.status === "failed" && !role) {
    return (
      <div className="mgmt-page">
        <div className="mg-body">
          <div className="mg-error">{roleState.error}</div>
        </div>
      </div>
    );
  }

  if (!role || !draft || !registry.data) {
    return (
      <div className="mgmt-page">
        <Loading text="Загрузка роли…" />
      </div>
    );
  }

  const totalResources = registry.data.sections.reduce((n, s) => n + s.resources.length, 0);
  const deletable = canDelete && !role.is_system && role.user_count === 0;

  return (
    <div className="mgmt-page">
      <div className="mg-header">
        <div>
          <button type="button" className="mg-back" onClick={goBack}>
            ← Все роли
          </button>
          <div className="mg-title">
            {role.label}
            {role.is_system && <span className="mg-badge">системная</span>}
            <span className="mg-badge mg-mono">{role.name}</span>
          </div>
          <div className="mg-subtitle">
            Сотрудников с ролью: {role.user_count}. Доступно разделов: {countReadable(draft)} из {totalResources}.
            {canWrite && " Клик по названию раздела отмечает всю строку, по заголовку — всю колонку."}
          </div>
        </div>
        {canDelete && !role.is_system && (
          <div className="mg-actions">
            <button
              type="button"
              className="mg-btn mg-btn--danger"
              disabled={!deletable}
              title={deletable ? "" : "Сначала снимите роль со всех сотрудников"}
              onClick={() => setConfirmDelete(true)}
            >
              Удалить роль
            </button>
          </div>
        )}
      </div>

      <div className="mg-body">
        {!canWrite && <div className="mg-hint">Режим просмотра: у вас нет права изменять роли.</div>}
        {error && <div className="mg-error">{error}</div>}
        {saved && !dirty && <div className="mg-success">Права сохранены.</div>}

        {canWrite && (
          <div className="mg-card">
            <div className="mg-form">
              <label className="mg-field">
                <span className="mg-label">Название роли</span>
                <input className="mg-input" value={label} onChange={(e) => setLabel(e.target.value)} />
              </label>
              <label className="mg-field">
                <span className="mg-label">Описание</span>
                <input
                  className="mg-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Кратко: за что отвечает роль"
                />
              </label>
            </div>
          </div>
        )}

        <PermissionMatrix
          registry={registry.data}
          value={draft}
          baseline={role.permissions}
          legend={{ added: "включено, ещё не сохранено", removed: "снято, ещё не сохранено" }}
          onChange={canWrite ? onMatrixChange : undefined}
        />

        {canWrite && dirty && (
          <div className="mg-savebar">
            <span className="mg-savebar-text">Есть несохранённые изменения</span>
            <div className="mg-actions">
              <button type="button" className="mg-btn" onClick={reset} disabled={saving}>
                Отменить
              </button>
              <button type="button" className="mg-btn mg-btn--primary" onClick={save} disabled={saving}>
                {saving ? "Сохраняем…" : "Сохранить"}
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        open={confirmDelete}
        title="Удалить роль?"
        message={`Роль «${role.label}» и её права будут удалены без возможности восстановления.`}
        confirmLabel="Удалить"
        danger
        onConfirm={remove}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
